'use client';

import { useEffect } from 'react';

/**
 * Registers the hand-written service worker at `/sw.js` (M4.1).
 *
 * Guards:
 *   - Only runs when `serviceWorker` is supported.
 *   - Skips non-production (dev/test) so HMR + jest aren't disrupted and we
 *     don't cache a dev build.
 *   - Registers after `load` so SW install never competes with first paint.
 *
 * Update flow is deliberately gentle: the SW calls `skipWaiting()` +
 * `clients.claim()` itself, so a new version takes over on the next navigation.
 * We do NOT force `location.reload()` here — that risks reload loops and would
 * interrupt active dashboard sessions. We only listen for an update to log it.
 */
export default function ServiceWorkerRegistrar() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator)) return;
    if (process.env.NODE_ENV?.trim() !== 'production') return;

    const register = () => {
      navigator.serviceWorker
        .register('/sw.js', { scope: '/' })
        .catch(() => {
          // Registration failures are non-fatal: the app works without the SW.
        });
    };

    if (document.readyState === 'complete') {
      register();
    } else {
      window.addEventListener('load', register, { once: true });
      return () => window.removeEventListener('load', register);
    }
  }, []);

  return null;
}
